import { WebStorageStateStore } from 'oidc-client-ts';

import { AppSettingsProvider } from '@britishcouncil/react-common';
import { OidcProvider, RequireAuth } from '@britishcouncil/react-oidc-auth';

import { DebouncedFullScreenLoader } from 'components';
import { Interceptors, PermissionsProvider, initSentry } from 'core';
import { AppSettings, AppSettingsContext, DEFAULT_APP_SETTINGS } from 'settings';

const getAppSettings = async () => {
  const env =
    import.meta.env.VITE_APP_ENV && import.meta.env.VITE_APP_ENV !== 'development'
      ? import.meta.env.VITE_APP_ENV
      : undefined;
  const appSettings = await loadAppSettings<AppSettings>('/appsettings.json');

  if (env) {
    appSettings.userManagerSettings.authority = `https://${env}-eamidentityadmin-dev.dev.britishcouncil.org`;
    appSettings.userManagerSettings.client_id = 'event-planner-test.app';
    appSettings.userManagerSettings.redirect_uri = `https://${env}.event-planner.test:82/login-successful`;
  }

  initSentry(appSettings);

  return appSettings;
};

const loadAppSettings = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url);
  const json = await response.json();
  return json as unknown as T;
};

export const AppSettingsProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <AppSettingsProvider
      defaultAppSettings={DEFAULT_APP_SETTINGS}
      Context={AppSettingsContext}
      appSettingsSource={getAppSettings}
      loader={<DebouncedFullScreenLoader text="One second, please..." />}
      error={<DebouncedFullScreenLoader text="Something went wrong, try refreshing the app." />}
    >
      <AppSettingsContext.Consumer>
        {({ userManagerSettings }) => (
          <OidcProvider
            {...userManagerSettings}
            userStore={new WebStorageStateStore({ store: window.localStorage })}
          >
            <Interceptors>
              <RequireAuth loader={DebouncedFullScreenLoader}>
                <PermissionsProvider>{children}</PermissionsProvider>
              </RequireAuth>
            </Interceptors>
          </OidcProvider>
        )}
      </AppSettingsContext.Consumer>
    </AppSettingsProvider>
  );
};
